import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Maximize2, Loader2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function ComplianceStatusWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchComplianceStatus();
  }, []);

  const fetchComplianceStatus = async () => {
    try {
      const result = await apiRequest('/compliance/dashboard');
      setData(result);
    } catch (error) {
      console.error('Failed to load compliance status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const overdue = data?.overdue_items || 0;
  const expiringSoon = data?.expiring_soon || 0;
  const totalRecords = data?.total_records || 0;
  const complianceRate = totalRecords > 0 ? ((totalRecords - overdue) / totalRecords * 100).toFixed(0) : 100;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="compliance-status-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-emerald-600" />
          <h3 className="font-semibold text-slate-900">Compliance Status</h3>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Compliance Rate */}
        <div className="text-center">
          <div className={`text-3xl font-bold ${complianceRate >= 90 ? 'text-green-600' : complianceRate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
            {complianceRate}%
          </div>
          <div className="text-sm text-slate-500">Compliance Rate</div>
        </div>

        {/* Status Cards */}
        <div className="space-y-2">
          {overdue > 0 && (
            <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-700 font-medium">{overdue} Overdue Items</span>
            </div>
          )}
          
          {expiringSoon > 0 && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-700 font-medium">{expiringSoon} Expiring Soon</span>
            </div>
          )}
          
          {overdue === 0 && expiringSoon === 0 && (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">All items up to date</span>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t border-slate-100">
          <div className="text-center">
            <div className="font-semibold text-slate-900">{totalRecords}</div>
            <div className="text-xs text-slate-500">Total Records</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-slate-900">{data?.active_records || 0}</div>
            <div className="text-xs text-slate-500">Active</div>
          </div>
        </div>
      </div>
    </div>
  );
}
